import {
  PaginatedTable,
  Button,
  Modal,
  absorbEvent,
  noop,
  useWindowContext,
  Status,
  Checkbox,
  Table,
} from '@iqueue/ui-kit'
import React, { useMemo, useCallback, useRef, useEffect, useState } from 'react'
import cn from 'classnames'
import ErrorView from './errors/ErrorView'
import Prompt from './prompt'
import useTranslate, { t } from '../hooks/useTranslate'
import { head, tail } from 'ramda'
import { DevEUIFormatter } from '../pages/sensor/SensorsPage'
import { listenSSE } from '../api/fetcher'
import SensorPresenter from '../api/sensorPresenter'

function UploadSensorModal({ open, setOpen, reload }) {
  const [file, setFile] = useState(null)
  const [isUploading, setIsUploading] = useState(false)
  const [schemaData, setSchemaData] = useState([])
  const locale = useTranslate()
  const { togglePaddings, setTitle, toggleScrollbars } = useWindowContext()
  const [showErrorsOnly, setShowErrorsOnly] = useState(false)

  const tableRef = useRef()
  const fileInputRef = useRef()

  // const translatedTitle = useMemo(() => t('menu.excelParse'), [locale])

  const schema = useMemo(() => {
    return [
      {
        key: 'devEUI',
        title: t('uploadExcel.devEUI'),
        render: (v,item) => {
          return <DevEUIFormatter devEUI={ v }/>
        },
      },
      {
        key: 'appKey',
        title: t('uploadExcel.appKey'),
        render: (v) => <span>{ v || 'N/A' }</span>,
        width: '15rem',
      },
      {
        key: 'devAddr',
        title: t('uploadExcel.devAddr'),
        width: '15rem',
        render: (v) => <span>{ v }</span>,
      },
      {
        key: 'limitsMax',
        title: t('uploadExcel.limits_max'),
        width: '15rem',
        render: (v) => <span>{ v }</span>,
      },
      {
        key: 'limitsMin',
        title: t('uploadExcel.limits_min'),
        width: '15rem',
        render: (v) => <span>{ v }</span>,
      },
      {
        key: 'name',
        title: t('uploadExcel.name'),
        width: '15rem',
        render: (v) => <b>{ v }</b>,
      },
      {
        key: 'status',
        title: t('uploadExcel.status'),
        width: '15rem',
        style: {
          position: 'sticky',
          right: 0,
        },
        render: (v, status) => {
          return <Status
            style={ { marginTop: '0.8rem' } }
            inline
            weak={ !status?.hasError }
            warning={ v === 'TECH_WORKS' }
            error={ !!status?.hasError }
          >
            { !status?.hasError ? t(`status.sensor.${ v }`) : t('uploadExcel.error') }
          </Status>
        },
      },
      {
        key: 'thresholdsCriticalMax',
        title: t('uploadExcel.thresholds_critical_max'),
        width: '20rem',
        render: (v) => <span>{ v }</span>,
      },
      {
        key: 'thresholdsCriticalMin',
        title: t('uploadExcel.thresholds_critical_min'),
        width: '20rem',
        render: (v) => <span>{ v }</span>,
      },
      {
        key: 'thresholdsWarnMax',
        title: t('uploadExcel.thresholds_warn_max'),
        width: '25rem',
        render: (v) => <span>{ v }</span>,
      },
      {
        key: 'thresholdsWarnMin',
        title: t('uploadExcel.thresholds_warn_min'),
        width: '25rem',
        render: (v) => <span>{ v }</span>,
      },
      {
        key: 'type',
        title: t('uploadExcel.type'),
        width: '20rem',
        render: (v) => v ? t(`type.sensor.${ v }`) : 'N/A',
      },
    ]
  }, [schemaData, locale])

  const tableData = useMemo(() => {
    return schemaData.filter((row) => {
      return showErrorsOnly ? row.hasError : true
    })
  }, [schemaData, showErrorsOnly])

  const getUploadFile = useCallback(async () => {
    await listenSSE('parse/excel/progress', (parsedData) => {
      const { keys, row, error } = parsedData

      const formattedData = keys.reduce((acc, key, index) => {
        acc[key] = row[index]
        return acc
      }, {})

      formattedData.hasError = !!error
      setIsUploading(false)
      setSchemaData((prevSchemaData) => [...prevSchemaData, formattedData])
    })
  }, [])

  const uploadFile = useCallback(async (file) => {
    try {
      setIsUploading(true)
      const fd = new FormData()
      fd.append('file', file)
      await SensorPresenter.post('parse/excel', fd, {}, false)
      getUploadFile().catch(err => console.error(err))
    } catch (e) {
      console.error(e)
    }
  }, [])

  const handleFileChange = useCallback((event) => {
    const file = event.target.files[0]
    if (file) {
      setFile(file)
      uploadFile(file).catch(err => console.error(err))
      event.target.value = ""
    }
  }, [uploadFile])

  const handleCheckboxChange = useCallback((event) => {
    setShowErrorsOnly(event)
  }, [])

  useEffect(() => {
    getUploadFile().catch(err => console.error(err))
  }, [])

  useEffect(() => {
    // setTitle(translatedTitle)
    togglePaddings(false)
    toggleScrollbars(true)
  }, [])

  return <>
    <Modal
      fullscreen={ true }
      title={ t('crud.add') }
      isOpened={ open }
      onClose={ () => {
        setOpen(false)
        setFile(null)
        setSchemaData([])
        setShowErrorsOnly(false)
        reload()
      } }
      footerActions={ [
        { key: 'close', danger: true, title: t('modal.editWellStatusModal.close') },
      ] }
    >

      { !file ? <div className="upload-container-modal">
        <label htmlFor="file-upload" className="file-upload-label">
          <input
            id="file-upload"
            type="file"
            accept=".xlsx, .xls"
            ref={ fileInputRef }
            onChange={ handleFileChange }
            disabled={ isUploading }
            style={ { display: 'none' } }
          />
          <h5 className="upload-plecholder">
            { t('uploadExcel.fileUpload') }
          </h5>
        </label>
      </div> : <div>
        <div className="checkbox-container">
          <Checkbox
            name="error"
            title={ t('uploadExcel.showErrorsOnly') }
            onChange={ handleCheckboxChange }
          />
        </div>
        { tableData.length !== 0 && <Table
          ref={ tableRef }
          indexable
          entries={ tableData }
          schema={ schema }
          minWidth="225rem"
        /> }
      </div> }
    </Modal>
  </>
}

export default function TableView({
  data,
  reload,
  reloadArgs = [],
  className,
  form,
  schema,
  presenter,
  checked,
  entity,
  permissions = [],
  modalProps = {},
  uploadItems = false,
  ...props
}) {
  const args = useRef({
    limit: 25,
    page: 1,
    ...(head(reloadArgs) || {}),
  })

  const [open, setOpen] = useState(false)

  const { canCreate, canUpdate, canDelete } = useMemo(() => {
    return {
      canCreate: permissions.includes('WRITE'),
      canUpdate: permissions.includes('UPDATE'),
      canDelete: permissions.includes('DELETE'),
    }
  }, [permissions])

  const handlePaginationChange = useCallback((...newArgs) => {
    args.current.page = newArgs[0]
    args.current.limit = newArgs[1]
    reload(args.current, tail(reloadArgs))
  }, [reload])

  const handleReload = useCallback(() => {
    reload(args.current, tail(reloadArgs))
  }, [reload, reloadArgs])

  const handleItemClick = useCallback((item = {}) => {
    const Form = form

    Modal
      .show({
        title: item.id ? t('crudTableView.update') : t('crudTableView.create'),
        children: () => <Form model={ item }/>,
        fullscreen: checked && true,
        onApply: presenter.upsert,
        errorView: ErrorView,
        footerActions: [{
          key: 'apply',
          submit: true,
          primary: true,
          title: item.id ? t('crudTableView.update') : t('crudTableView.create'),
        }, {
          key: 'cancel',
          danger: true,
          title: t('crudTableView.cancel'),
        }],
        ...modalProps,
      })
      .then(handleReload)
      .catch(noop)
  }, [form, t, presenter, handleReload, modalProps])

  const fullSchema = useMemo(() => {
    if (!canDelete) return schema

    return [
      ...schema,
      {
        key: 'delete',
        width: '4.5rem',
        render: (_, item) => <Button
          icon={ 'delete' }
          danger
          onClick={ async (e) => {
            absorbEvent(e)
            const ret = await Prompt(t('message.confirmDelete'), {
              danger: true,
              onApply: () => presenter.remove(item),
            })

            if (ret !== false) {
              handleReload()
            }
          } }
        />,
      },
    ]
  }, [schema, t, canDelete, handleReload])

  useEffect(() => {
    args.current = {
      ...args.current,
      ...(head(reloadArgs) || {}),
    }
  }, [reloadArgs])

  return <PaginatedTable
    indexable
    dataSorted
    sizes={ [15, 25, 50, 100, 150, 500] }
    defaultSize={ data?.limit || 10 }
    defaultPage={ data?.page || 1 }
    total={ data?.total || 50 }
    className={ cn('borderless', className, {
      'readonly': !canUpdate,
    }) }
    schema={ fullSchema }
    minWidth={ '0rem' } // TODO deprecate it in lib!
    entries={ data?.entries || [] }
    onPageChanged={ handlePaginationChange }
    onRowClick={ canUpdate ? handleItemClick : noop }
    { ...props }
  >

    { <UploadSensorModal
      open={ open }
      setOpen={ setOpen }
      reload={ reload }
    /> }

    { canCreate && <div>
      <Button primary onClick={ () => {
        if (!uploadItems) {
          handleItemClick()
        } else {
          setOpen(true)
        }
      } }>
        { `${ t('crud.add') } ${ t(`entity.${ entity }.singular`) }` }
      </Button>

    </div> }
  </PaginatedTable>
}
